/*
 * collection-filter-boost.js  —  ZEHN   (v2)
 * -----------------------------------------------------------------------------
 * Speeds up every "click something, wait for the page to catch up" interaction:
 *
 *   A. Collection category drilldown  (<collection-drilldown>)
 *   B. Collection facet filters       (<zehn-facets>)
 *   C. Product page colour/size picker(<variant-picker>)
 *   D. "Produktdetails ansehen" modal gallery (.pdp-modal)
 *
 * It patches the theme's components at runtime instead of rewriting their
 * source files, so a theme update won't clobber it and removing one script tag
 * reverts everything.
 *
 * ---------------------------------------------------------------------------
 * MEASURED PROBLEMS THIS FIXES (preview store, /collections/all and
 * /products/outpost-performance-cargo-shorts-3)
 *
 *  A/B  Collection filtering
 *       - 194 KB per tap, no loading feedback for 1-3 s, no caching, and
 *         98 KB of that payload was inline <style> re-parsed on every tap.
 *
 *  C    Product page variant picker
 *       - Each colour click fetched the ENTIRE product page: 530 KB decoded /
 *         76 KB gzipped, ~1.27 s. Horizon only scopes the request to a section
 *         inside quick-add and product cards; on the product page itself it
 *         omits section_id. Adding it: 530 KB -> 265 KB, ~1270 ms -> ~800 ms.
 *       - No caching, so going back to a colour you already viewed refetched
 *         the whole thing again.
 *
 *  D    PDP modal gallery
 *       - All 49 slides are loading="lazy" inside a horizontal scroller, and
 *         the track keeps its scroll offset when you switch colour (measured
 *         scrollLeft 714 of 4626). So you land mid-carousel on slides that have
 *         never been fetched and stare at blank frames until you swipe.
 *
 * ---------------------------------------------------------------------------
 * INSTALL / UPDATE
 *   Replaces the v1 file of the same name — re-upload over it in Assets and
 *   you're done, no theme.liquid change needed.
 *
 *   If installing fresh:
 *     1. assets/collection-filter-boost.js   (this file)
 *     2. assets/collection-filter-boost.css
 *     3. layout/theme.liquid, before </head>:
 *          {{ 'collection-filter-boost.css' | asset_url | stylesheet_tag }}
 *          <script src="{{ 'collection-filter-boost.js' | asset_url }}" defer></script>
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ config */

  var CONFIG = {
    cacheEntries: 24,
    cacheTtlMs: 5 * 60 * 1000,
    prefetchOnIntent: true,
    prefetchPrimaryRowOnIdle: true,
    hoistStylesOver: 800,
    gallerySlidesEager: 5      // slides warmed immediately after a colour change
  };

  var connection = navigator.connection || {};
  var THRIFTY =
    connection.saveData === true ||
    /(^|-)(2g|slow-2g)$/.test(connection.effectiveType || '');

  var idle = window.requestIdleCallback
    ? window.requestIdleCallback.bind(window)
    : function (fn) { return setTimeout(fn, 800); };

  /* ------------------------------------------------------- section-html cache */

  var nativeFetch = window.fetch.bind(window);
  var cache = new Map(); // url -> { at, promise }

  /* URLs worth caching: section renders and variant re-renders. */
  function isCacheable(url) {
    return typeof url === 'string' &&
      (url.indexOf('section_id=') !== -1 || url.indexOf('option_values=') !== -1);
  }

  function loadHTML(url, signal) {
    var hit = cache.get(url);
    if (hit && Date.now() - hit.at < CONFIG.cacheTtlMs) {
      cache.delete(url);        // refresh recency for LRU eviction
      cache.set(url, hit);
      return withSignal(hit.promise, signal);
    }

    var promise = nativeFetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .catch(function (error) {
        cache.delete(url);      // never cache a failure
        throw error;
      });

    cache.set(url, { at: Date.now(), promise: promise });
    while (cache.size > CONFIG.cacheEntries) {
      cache.delete(cache.keys().next().value);
    }
    return withSignal(promise, signal);
  }

  /*
   * A cached response must still honour the caller's AbortController, otherwise
   * a superseded variant request would resolve and morph stale HTML over a
   * newer selection. <variant-picker> aborts in-flight requests on every click,
   * so this matters.
   */
  function withSignal(promise, signal) {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(abortError());
    return new Promise(function (resolve, reject) {
      var onAbort = function () { reject(abortError()); };
      signal.addEventListener('abort', onAbort, { once: true });
      promise.then(
        function (value) { signal.removeEventListener('abort', onAbort); resolve(value); },
        function (error) { signal.removeEventListener('abort', onAbort); reject(error); }
      );
    });
  }

  function abortError() {
    try { return new DOMException('Aborted', 'AbortError'); }
    catch (e) { var err = new Error('Aborted'); err.name = 'AbortError'; return err; }
  }

  /*
   * One hook covers the drilldown, <zehn-facets>, <variant-picker>, the
   * quick-add sheet and our own prefetches. Everything else goes straight to
   * the browser's fetch untouched.
   */
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : input && input.url;
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();

    if (method === 'GET' && isCacheable(url)) {
      return loadHTML(url, init && init.signal).then(function (html) {
        return new Response(html, {
          status: 200,
          headers: { 'Content-Type': 'text/html; charset=utf-8' }
        });
      });
    }
    return nativeFetch(input, init);
  };

  /* ------------------------------------------------------------ style hoisting */

  var hoisted = new Set();

  /*
   * A ?section_id= response inlines every {% stylesheet %} block the section
   * and its snippets declare — 98 KB here, versus 2.7 KB in the same section on
   * a normal page load. Moving those blocks to <head> once and deleting the
   * duplicates from later responses stops the browser re-parsing them and
   * re-running a full style recalculation on every filter tap.
   */
  function hoistStyles(root) {
    if (!root) return;
    var blocks = root.querySelectorAll('style');
    for (var i = 0; i < blocks.length; i++) {
      var style = blocks[i];
      var css = style.textContent || '';
      if (css.length < CONFIG.hoistStylesOver) continue; // leave tiny per-card blocks in place

      if (hoisted.has(css)) {
        style.remove();
      } else {
        hoisted.add(css);
        style.setAttribute('data-hoisted-by', 'collection-filter-boost');
        document.head.appendChild(style);
      }
    }
  }

  /* ------------------------------------------------------------------ helpers */

  function sectionEndpoint(url, sectionId) {
    var target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    return target.toString();
  }

  function unitOf(host) {
    if (host.unit) return host.unit;
    var selector = ((host.dataset && host.dataset.resultsSelector) || '').trim();
    var target = selector ? document.querySelector(selector) : null;
    return target ? target.closest('[id^="shopify-section-"]') : null;
  }

  function markBusy(unit, busy) {
    if (!unit) return;
    if (busy) {
      unit.setAttribute('data-filtering', '');
      unit.setAttribute('aria-busy', 'true');
    } else {
      unit.removeAttribute('data-filtering');
      unit.removeAttribute('aria-busy');
    }
  }

  /* ------------------------------------------------- A. drilldown (collection) */

  function patchDrilldown() {
    var Drilldown = customElements.get('collection-drilldown');
    if (!Drilldown || Drilldown.prototype.__boosted) return;
    Drilldown.prototype.__boosted = true;

    Drilldown.prototype.navigate = function (url, push) {
      var self = this;
      if (!url) return;

      var unit = unitOf(this);
      if (!this.useAjax || !unit) {
        window.location.href = url;
        return;
      }

      if (push) {
        try { window.history.pushState({ drilldown: this.selected }, '', url); }
        catch (error) { /* history unavailable in some in-app browsers */ }
      }

      var unitId = unit.id;
      var endpoint = sectionEndpoint(url, unitId.replace(/^shopify-section-/, ''));
      var token = {};
      this.fetchToken = token;

      // Instant feedback — the single biggest perceived-speed win.
      markBusy(unit, true);

      loadHTML(endpoint)
        .then(function (html) {
          if (self.fetchToken !== token) return; // a newer tap won

          var doc = new DOMParser().parseFromString(html, 'text/html');
          var next = doc.getElementById(unitId) || doc.querySelector('[id^="shopify-section-"]');
          if (!next) throw new Error('Matching section not found in response');

          hoistStyles(next);

          next.setAttribute('data-filter-entering', '');
          unit.replaceWith(next);
          self.unit = next;

          requestAnimationFrame(function () {
            next.removeAttribute('data-filter-entering');
          });
        })
        .catch(function () {
          if (self.fetchToken !== token) return;
          markBusy(unit, false);
          window.location.href = url; // hard-navigate fallback, as before
        });
    };
  }

  /* ------------------------------------------------------ B. facets (feedback) */

  function patchFacets() {
    var Facets = customElements.get('zehn-facets');
    if (!Facets || Facets.prototype.__boosted) return;
    Facets.prototype.__boosted = true;

    var original = Facets.prototype.navigate;
    if (typeof original !== 'function') return;

    // The element already sets data-loading on itself, but nothing on the page
    // reacts to it. Mirror the state onto the results section so the grid dims.
    Facets.prototype.navigate = function (url, openState) {
      var unit = this.closest('[id^="shopify-section-"]');
      markBusy(unit, true);
      var done = function () { markBusy(unit, false); };
      var result = original.call(this, url, openState);
      if (result && typeof result.finally === 'function') return result.finally(done);
      if (result && typeof result.then === 'function') return result.then(done, done);
      done();
      return result;
    };
  }

  /* ------------------------------------------------ C. variant picker (product) */

  function patchVariantPicker() {
    var VariantPicker = customElements.get('variant-picker');
    if (!VariantPicker || VariantPicker.prototype.__boosted) return;
    VariantPicker.prototype.__boosted = true;

    var original = VariantPicker.prototype.buildRequestUrl;
    if (typeof original !== 'function') return;

    VariantPicker.prototype.buildRequestUrl = function (selectedOption) {
      var url = original.apply(this, arguments);
      try {
        // Quick-add and product-card pickers already scope themselves to
        // section-rendering-product-card — leave those alone.
        if (!url || url.indexOf('section_id=') !== -1) return url;

        var sectionId = this.dataset.sectionId;
        if (!sectionId) return url;

        // A combined-listing swatch points at a DIFFERENT product, whose page
        // uses a different section id. Scoping it would return nothing.
        if (selectedOption && selectedOption.dataset && selectedOption.dataset.connectedProductUrl) {
          return url;
        }

        var target = new URL(url, window.location.origin);
        if (target.pathname !== window.location.pathname) return url; // not this product's page

        target.searchParams.set('section_id', sectionId);
        return target.pathname + target.search;
      } catch (error) {
        return url; // any surprise: fall back to the theme's own URL
      }
    };

    /*
     * The picker gives no feedback while it waits, so a colour tap looks like
     * nothing happened. Mark the product section busy for the duration.
     * loadHTML() shares the in-flight promise with the picker's own fetch, so
     * this adds no extra request.
     */
    var originalFetch = VariantPicker.prototype.fetchUpdatedSection;
    if (typeof originalFetch !== 'function') return;

    VariantPicker.prototype.fetchUpdatedSection = function (requestUrl) {
      var unit = this.closest('[id^="shopify-section-"]');
      var result = originalFetch.apply(this, arguments);
      if (unit && requestUrl) {
        unit.setAttribute('data-variant-loading', '');
        var clear = function () { unit.removeAttribute('data-variant-loading'); };
        loadHTML(requestUrl).then(function () {
          requestAnimationFrame(clear);
        }, clear);
      }
      return result;
    };
  }

  /*
   * Builds the URL a given option WOULD request, so it can be warmed on
   * pointerdown. If the guess is ever wrong the prefetch is simply never read —
   * the real request still goes through buildRequestUrl, and the cache is keyed
   * by exact URL.
   */
  function variantPrefetchUrl(picker, input) {
    if (!input.dataset || input.dataset.connectedProductUrl) return null;

    var sectionId = picker.dataset.sectionId;
    var productUrl = picker.dataset.productUrl;
    if (!sectionId || !productUrl) return null;

    var fieldsets = picker.querySelectorAll('fieldset');
    if (!fieldsets.length) return null;

    var fieldsetIndex = Number(input.dataset.fieldsetIndex);
    var values = [];
    for (var i = 0; i < fieldsets.length; i++) {
      var chosen = i === fieldsetIndex ? input : fieldsets[i].querySelector('input:checked');
      var id = chosen && chosen.dataset && chosen.dataset.optionValueId;
      if (id) values.push(id);
    }
    if (!values.length) return null;

    var url = new URL(productUrl, window.location.origin);
    url.searchParams.set('option_values', values.join(','));
    url.searchParams.set('section_id', sectionId);
    return url.pathname + url.search;
  }

  /* ------------------------------------------------ D. PDP modal gallery warming */

  var slidesByOption = new Map(); // option handle -> array of slide elements

  function activeSlides(modal) {
    var all = modal.querySelectorAll('.pdp-media');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      // Inactive slides are collapsed to 0x0 rather than removed.
      if (all[i].getBoundingClientRect().width > 0) out.push(all[i]);
    }
    return out;
  }

  /*
   * Flipping loading="lazy" to "eager" starts the download immediately, which
   * is what the slides need: they sit in a horizontal scroller and the lazy
   * loader won't touch them until they're swiped into view.
   */
  function warmSlides(slides, count) {
    var limit = Math.min(count, slides.length);
    for (var i = 0; i < limit; i++) {
      var img = slides[i].querySelector('img');
      if (!img || img.dataset.zWarmed) continue;
      img.dataset.zWarmed = '1';

      if (img.complete && img.naturalWidth > 0) {
        img.dataset.zLoaded = '1';
        continue;
      }
      img.addEventListener('load', function () { this.dataset.zLoaded = '1'; }, { once: true });

      if ('fetchPriority' in img) img.fetchPriority = i < 2 ? 'high' : 'low';
      img.loading = 'eager';

      /*
       * Flipping loading to "eager" starts the download in Chromium, but not
       * every engine re-evaluates an already-parsed image. A detached Image
       * with the same srcset/sizes picks the identical candidate and fills the
       * HTTP cache, so the real <img> paints from cache whichever path wins.
       */
      try {
        var preload = new Image();
        var sizes = img.getAttribute('sizes');
        var srcset = img.getAttribute('srcset');
        if (sizes) preload.sizes = sizes;
        if (srcset) preload.srcset = srcset;
        preload.src = img.getAttribute('src') || '';
      } catch (error) { /* preloading is best-effort */ }
    }
  }

  function onGalleryOptionClick(event) {
    var option = event.target.closest && event.target.closest('[data-pdp-opt]');
    if (!option) return;
    var modal = option.closest('[data-pdp-modal]');
    if (!modal) return;

    var handle = option.getAttribute('data-pdp-value-handle');
    var known = handle && slidesByOption.get(handle);
    if (known) warmSlides(known, CONFIG.gallerySlidesEager); // seen before: start now

    // Let the theme's own handler re-collapse the track first.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var track = modal.querySelector('[data-pdp-track]');
        if (!track) return;

        var slides = activeSlides(modal);
        if (!slides.length) return;
        if (handle) slidesByOption.set(handle, slides);

        warmSlides(slides, CONFIG.gallerySlidesEager);

        // Land on the first slide of the new colour instead of keeping the old
        // scroll offset, which drops you onto slides that were never fetched.
        try { track.scrollTo({ left: 0, behavior: 'auto' }); }
        catch (error) { track.scrollLeft = 0; }

        if (!THRIFTY) idle(function () { warmSlides(slides, slides.length); });
      });
    });
  }

  function warmOpenGallery() {
    var modal = document.querySelector('[data-pdp-modal]');
    if (!modal || !modal.querySelector('[data-pdp-track]')) return;
    var slides = activeSlides(modal);
    if (slides.length) warmSlides(slides, CONFIG.gallerySlidesEager);
  }

  /* ----------------------------------------------------------------- prefetch */

  var prefetched = new Set();

  function warm(url) {
    if (!url || prefetched.has(url)) return;
    prefetched.add(url);
    loadHTML(url).catch(function () { prefetched.delete(url); });
  }

  function prefetchPill(pill) {
    if (!pill) return;
    var href = pill.getAttribute('href');
    if (!href) return;
    var host = pill.closest('collection-drilldown');
    if (!host) return;
    var unit = unitOf(host);
    if (!unit) return;
    warm(sectionEndpoint(href, unit.id.replace(/^shopify-section-/, '')));
  }

  function prefetchVariant(label) {
    if (!label) return;
    var picker = label.closest('variant-picker');
    if (!picker) return;
    var input = label.querySelector('input') ||
      (label.htmlFor && document.getElementById(label.htmlFor));
    if (!input || input.checked) return;
    warm(variantPrefetchUrl(picker, input));
  }

  function onIntent(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    prefetchPill(target.closest('.cdd-pill, .bc-link[data-id]'));
    prefetchVariant(target.closest('variant-picker label'));

    // Warm a PDP colour we've already measured, before the click lands.
    var option = target.closest('[data-pdp-opt]');
    if (option) {
      var known = slidesByOption.get(option.getAttribute('data-pdp-value-handle'));
      if (known) warmSlides(known, CONFIG.gallerySlidesEager);
    }
  }

  function warmPrimaryRow() {
    var row = document.querySelector('collection-drilldown [data-row="primary"]');
    if (!row) return;
    var pills = row.querySelectorAll('.cdd-pill');
    for (var i = 0; i < pills.length && i < 6; i++) prefetchPill(pills[i]);
  }

  /* --------------------------------------------------------------------- init */

  function init() {
    var drilldown = document.querySelector('collection-drilldown');
    if (drilldown) hoistStyles(unitOf(drilldown));

    ['collection-drilldown', 'zehn-facets', 'variant-picker'].forEach(function (name) {
      customElements.whenDefined(name).then(function () {
        if (name === 'collection-drilldown') patchDrilldown();
        else if (name === 'zehn-facets') patchFacets();
        else patchVariantPicker();
      }).catch(function () { /* element absent on this template — fine */ });
    });

    document.addEventListener('click', onGalleryOptionClick, true);

    if (CONFIG.prefetchOnIntent && !THRIFTY) {
      var options = { capture: true, passive: true };
      document.addEventListener('pointerdown', onIntent, options);
      document.addEventListener('touchstart', onIntent, options);
      document.addEventListener('mouseover', onIntent, options);
    }

    var onLoad = function () {
      idle(function () {
        if (CONFIG.prefetchPrimaryRowOnIdle && !THRIFTY) warmPrimaryRow();
        warmOpenGallery();
      }, { timeout: 3000 });
    };
    if (document.readyState === 'complete') onLoad();
    else window.addEventListener('load', onLoad, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();